import type { IconName } from '@usealloy/component-library';
import { Icon } from '@usealloy/component-library';
import React from 'react';
import styled from 'styled-components';

import { bodySmallText, truncateText } from './utils/mixins';
import { SidebarButtonBase } from './SidebarButtonBase';
import { useSidebarCtx } from './SidebarContext';

// ============================================================
//     SIDEBAR GROUP - WRAPPER
// ============================================================
const StyledGroup = styled.div({
  position: 'relative',
  width: '100%',
});

/**
 * Logical grouping container for organizing related menu items.
 *
 * Use to separate navigation into distinct sections with optional labels and actions.
 *
 * @example
 * ```tsx
 * <Sidebar.Group>
 *   <Sidebar.GroupLabel>Navigation</Sidebar.GroupLabel>
 *   <Sidebar.Menu>
 *     <Sidebar.MenuItem>
 *       <Sidebar.MenuButton>Home</Sidebar.MenuButton>
 *     </Sidebar.MenuItem>
 *   </Sidebar.Menu>
 * </Sidebar.Group>
 * ```
 */
export const Group = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<'div'>>((props, forwardedRef) => <StyledGroup {...props} ref={forwardedRef} data-component="Sidebar.Group" />);

Group.displayName = 'Sidebar.Group' as const;

// ============================================================
//     SIDEBAR GROUP - LABEL
// ============================================================
const StyledGroupLabel = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--cl-space-2)',
  flex: '0 0 auto',

  width: '100%',
  paddingBlock: 'var(--cl-space-1)',
  paddingInline: 'var(--cl-space-1)',

  color: 'var(--cl-text-muted)',
  ...bodySmallText,

  '& > span': {
    ...truncateText,
  },

  '&[data-expanded="false"]': {
    paddingInline: 0,
    justifyContent: 'center',

    // We transition BOTH padding and justify content when moving to collapsed, but only transition padding when moving to expanded
    transitionProperty: 'padding-inline, justify-content',
    transitionDelay: '250ms',
  },

  transitionProperty: 'padding-inline',
  transitionDuration: 'var(--cl-duration-300)',
  transitionTimingFunction: 'var(--cl-motion-ease-in-out)',
});

/**
 * Heading/label for a `Sidebar.Group` section.
 *
 * Renders muted text that identifies the group's purpose.
 *
 * NOTE: Wrap text content in a `<span>` for proper text truncation when the sidebar is collapsed.
 *
 * @example
 * ```tsx
 * <Sidebar.Group>
 *   <Sidebar.GroupLabel>
 *     <span>Navigation</span>
 *   </Sidebar.GroupLabel>
 *   <Sidebar.Menu>
 *   // ...Menu Items
 *   </Sidebar.Menu>
 * </Sidebar.Group>
 * ```
 */
export const GroupLabel = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<'div'>>((props, forwardedRef) => {
  const { isExpanded } = useSidebarCtx();

  return (
    <StyledGroupLabel {...props} ref={forwardedRef} data-component="Sidebar.GroupLabel" data-expanded={isExpanded} />
  );
});

GroupLabel.displayName = 'Sidebar.GroupLabel' as const;

// ============================================================
//     SIDEBAR GROUP - ACTION
// ============================================================
interface GroupActionProps
  extends Omit<React.ComponentPropsWithoutRef<'button'>, 'children'> {
  /**
   * The specific icon to render for the action button
   */
  iconName: IconName;

  /**
   * Required for accessibility
   */
  label: string;
}

const StyledGroupAction = styled(SidebarButtonBase)({
  display: 'flex',
  height: 'var(--cl-space-4)',
  width: 'var(--cl-space-4)',
  border: 'none',
  marginRight: 'var(--cl-space-2)',

  '--btn-bg': 'var(--cl-background-tertiary)',
  '--btn-bg-hover': 'var(--cl-background-tertiary-hovered)',
  '--btn-bg-active': 'var(--cl-background-tertiary-active)',

  '&[data-expanded="false"]': {
    display: 'none',
  },
});

/**
 * Action button displayed within a `Sidebar.GroupLabel` header, adjacent to any text content.
 *
 * Hidden when the sidebar is collapsed.
 * Useful for a group-level action, like "Add new item" or "Expand all".
 *
 * #### Important Props:
 * - `iconName` - the icon to display
 * - `label` - required accessible label for the button
 *
 * @example
 * ```tsx
 * <Sidebar.Group>
 *   <Sidebar.GroupLabel>
 *     <span>Navigation</span>
 *     <Sidebar.GroupAction iconName={IconName.Plus} label="Add new item" />
 *   </Sidebar.GroupLabel>
 *   <Sidebar.Menu>
 *   // ...Menu Items
 *   </Sidebar.Menu>
 * </Sidebar.Group>
 * ```
 */
export const GroupAction = (props: GroupActionProps) => {
  const { iconName, label, ...restProps } = props;
  const { isExpanded } = useSidebarCtx();

  return (
    <StyledGroupAction
      type="button"
      {...restProps}
      aria-label={label}
      data-component="Sidebar.GroupAction"
      data-expanded={isExpanded}
      data-icon-only=""
      data-size="xsmall"
    >
      <Icon icon={iconName} size="small" />
    </StyledGroupAction>
  );
};

GroupAction.displayName = 'Sidebar.GroupAction' as const;
